import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap } from 'rxjs';
import { SharedActions } from '@states/shared/shared.action-types';
import { PtzPresetActions } from '@states/ptz-presets/ptz-preset.actions';
import { CamerasService } from '../../cameras/cameras.service';
import { UtilsService } from '../../edge/utils.service';

function ptzPresetErrorMessage(
  response: HttpErrorResponse,
  utilsService: UtilsService,
  translateService: TranslateService,
): string {
  if (response.error?.code === 'PTZ_INTRA_BUS_UNAVAILABLE') {
    return String(
      translateService.instant(
        'CAMERA_EDIT_PTZ.AUTOMATION.ERRORS.PTZ_INTRA_BUS_UNAVAILABLE',
      ),
    );
  }

  return (
    utilsService.errMessage(response) ||
    translateService.instant('CAMERA_EDIT_PTZ.AUTOMATION.ERRORS.PRESET_REQUEST_FAILED')
  );
}

export const loadPtzPresets$ = createEffect(
  (actions$ = inject(Actions), camerasService = inject(CamerasService)) =>
    actions$.pipe(
      ofType(PtzPresetActions.loadPresets),
      switchMap(({ request }) =>
        camerasService.getPtzPresets(request).pipe(
          map((presets) => PtzPresetActions.loadPresetsSuccess({ presets })),
          catchError(() => of(PtzPresetActions.loadPresetsFail())),
        ),
      ),
    ),
  { functional: true },
);

export const createPtzPreset$ = createEffect(
  (
    actions$ = inject(Actions),
    camerasService = inject(CamerasService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PtzPresetActions.createPreset),
      switchMap(({ request, name }) =>
        camerasService.createPtzPreset(request, { name }).pipe(
          switchMap(() => [
            PtzPresetActions.createPresetSuccess(),
            PtzPresetActions.loadPresets({ request }),
          ]),
          catchError((response) =>
            of(
              PtzPresetActions.createPresetFail(),
              SharedActions.showMessage({
                error: ptzPresetErrorMessage(response, utilsService, translateService),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const renamePtzPreset$ = createEffect(
  (
    actions$ = inject(Actions),
    camerasService = inject(CamerasService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PtzPresetActions.renamePreset),
      switchMap(({ request, presetId, name }) =>
        camerasService.updatePtzPreset(request, presetId, { name }).pipe(
          switchMap(() => [
            PtzPresetActions.renamePresetSuccess(),
            PtzPresetActions.loadPresets({ request }),
          ]),
          catchError((response) =>
            of(
              PtzPresetActions.renamePresetFail(),
              SharedActions.showMessage({
                error: ptzPresetErrorMessage(response, utilsService, translateService),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const redefinePtzPresetPosition$ = createEffect(
  (
    actions$ = inject(Actions),
    camerasService = inject(CamerasService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PtzPresetActions.redefinePresetPosition),
      switchMap(({ request, presetId }) =>
        camerasService.redefinePtzPreset(request, presetId).pipe(
          switchMap(() => [
            PtzPresetActions.redefinePresetPositionSuccess(),
            PtzPresetActions.loadPresets({ request }),
          ]),
          catchError((response) =>
            of(
              PtzPresetActions.redefinePresetPositionFail(),
              SharedActions.showMessage({
                error: ptzPresetErrorMessage(response, utilsService, translateService),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const deletePtzPreset$ = createEffect(
  (
    actions$ = inject(Actions),
    camerasService = inject(CamerasService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PtzPresetActions.deletePreset),
      switchMap(({ request, presetId, confirmed }) =>
        camerasService.deletePtzPreset(request, presetId, confirmed).pipe(
          switchMap(() => [
            PtzPresetActions.deletePresetSuccess(),
            PtzPresetActions.loadPresets({ request }),
          ]),
          catchError((response) =>
            of(
              PtzPresetActions.deletePresetFail(),
              SharedActions.showMessage({
                error: ptzPresetErrorMessage(response, utilsService, translateService),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const setPtzPresetHomePreset$ = createEffect(
  (
    actions$ = inject(Actions),
    camerasService = inject(CamerasService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(PtzPresetActions.setHomePreset),
      switchMap(({ request, presetId }) =>
        camerasService.setPtzPresetHomePreset(request, presetId).pipe(
          switchMap(() => [
            PtzPresetActions.setHomePresetSuccess(),
            PtzPresetActions.loadPresets({ request }),
          ]),
          catchError((response) =>
            of(
              PtzPresetActions.setHomePresetFail(),
              SharedActions.showMessage({
                error: ptzPresetErrorMessage(response, utilsService, translateService),
              }),
            ),
          ),
        ),
      ),
    ),
  { functional: true },
);
